/* eslint-disable */
import React, { useEffect, useRef, useState } from 'react';
import {
  FormProvider, SubmitHandler, useForm,
} from 'react-hook-form';
import { AxiosError } from "axios";
import * as yup from 'yup';
import useYupValidationResolver from 'src/hooks/useYupValidationResolver';
import RHFInput from 'src/components/elements/Input/RHFInput';
import RHFSelect from 'src/components/elements/Select/RHFSelect';
import RHFEditor from 'src/components/elements/Editor/RHFEditor';
import { Button, Paper } from '@mui/material';
import RHFFile from 'src/components/elements/File/RHFFile';
import { FileFormat } from 'src/components/elements/File';
import RHFMultiAutoComplete from 'src/components/elements/MultiAutoComplete/RHFMultiAutoComplete';
import { useClients } from 'src/hooks/query/clients';
import { useRecruiters } from 'src/hooks/query/recruiters';
import useSkills from 'src/hooks/query/skills';
import { useLocations } from 'src/hooks/query/locations';
import { DialogContentProps } from 'src/components/Dialog/Dialog';
import { scrollToError } from 'src/utils/FormUtils';
import API from '../../../../network';
import { Option, options } from 'src/components/elements/Input';

interface CreateJobProps extends DialogContentProps {
  onCreateJob: (data: any, close: (() => void) | undefined) => Promise<void>;
}

export interface CreateJobFormValues {
  clientId: number;
  jobId: string;
  jobTitle: string;
  requirementType: string;
  vendor: string;
  jobType: string;
  jobDuration: string;
  payRate: string;
  jobLocations: [];
  skills: [];
  jobOrderDescription: {};
  documents: any;
  jobOrderRecruiters: [];
  currency: Option,
}

const validationSchema = yup.object().shape({
  clientId: yup.number().required('Client Required'),
  jobId: yup
    .string().required('Job Id Required'),
  jobTitle: yup.string().typeError('Enter Job Title').required('Job Title Required'),
  requirementType: yup.string().required('Requirement Type Required'),
  vendor: yup
    .string()
    .test('isC2COrBenchSales', 'Vendor Required', function(value) {
      const requirementType = this.parent.requirementType;
      if ((requirementType === 'c2c' || requirementType === 'benchSales') && !value) {
        throw new yup.ValidationError('Vendor Required', value, 'vendor');
      }
      return true;
    })
    .nullable(),
  jobLocations: yup.array().min(1, 'Job Location Required').required('Job Location Required'),
  skills: yup.array().min(1, 'Select Skill').required('Select Skill'),
  jobOrderDescription: yup.string().required('Job Descripion Required'),
  jobOrderRecruiters: yup.array().min(1, 'Select Recruiter').required('Select Recruiter'),
});

const CreateJob = function CreateJob(props: CreateJobProps) {
  const { onCreateJob, close, showDialogLoading, hideDialogLoading, } = props;
  const {
    clientOptions,
    clientsLoading,
    fetchClients,
  } = useClients({enabled: false});

  const {
    recruitersWithEmailOptions,
    recruitersLoading,
    fetchRecruiters,
  } = useRecruiters({enabled: false});

  const {
    skillsOptions,
    skillsLoading,
    fetchSkills,
  } = useSkills({enabled: false});

  const {
    locationsOptions,
    locationsLoading,
    fetchLocations,
  } = useLocations({enabled: false});

  const [enableVendor, SetEnableVendor] = useState(true);
  const [enableDuration, setEnableDuration] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<Option>(options[0]);
  const [disableSubmitBtn, setDisableSubmitBtn] = useState(false);

  const resolver = useYupValidationResolver(validationSchema);
  const methods = useForm<CreateJobFormValues>({
    resolver,
  });
  const {
    handleSubmit,
    watch,
    getValues,
    setError,
    clearErrors,
  } = methods;

  const { jobId, clientId } = watch();

  const checkDuplicateJob = async () => {
    clearErrors('jobId');
    if (jobId) {
      const payload = {
        jobId: jobId,
        clientId: getValues('clientId'),
      };
      try {
        await API.getInstance().checkDuplicateJobId(payload);
      } catch (e) {
        if (e instanceof AxiosError && e.response?.status === 409) {
          setError('jobId', { type: 'required', message: 'Job Id cannot be duplicate' });
          return false;
        }
      }
    }
    return true;
  };

  useEffect(() => {
    //check if jobId already exists
    checkDuplicateJob();
  }, [jobId, clientId]);

  useEffect(() => {
    const requirementType = methods.watch('requirementType');
    if (requirementType === 'c2c' || requirementType === 'benchSales') {
      SetEnableVendor(false);
    } else {
      SetEnableVendor(true);
      if (getValues('vendor')) {
        methods.setValue('vendor', '');
      }
    }
  }, [methods.watch('requirementType')]);

  useEffect(() => {
    if (methods.watch('jobType') === 'fullTime') {
      setEnableDuration(true);
      if (getValues('jobDuration')) {
        methods.setValue('jobDuration', '');
      }
    } else {
      setEnableDuration(false);
    }
  }, [methods.watch('jobType')]);

  useEffect(() => {
    if (locationsOptions.length === 0) fetchLocations();
  },[]);

  useEffect(() => {
    if (skillsOptions.length === 0) fetchSkills();
  },[]);

  useEffect(() => {
    if (clientOptions.length === 0) fetchClients();
  },[]);

  useEffect(() => {
    if (recruitersWithEmailOptions.length === 0) fetchRecruiters();
  },[]);

  const submitHandler: SubmitHandler<CreateJobFormValues> = async (data) => {
    try {
      const isValid = await checkDuplicateJob();
      if (!isValid){
        onError();
        return;
      }
      const jobLocations = data?.jobLocations.map(location => ({ location: location }));
      const jobOrderDescription = { jobDescription: data?.jobOrderDescription }
      const jobOrderRecruiters = data?.jobOrderRecruiters.map(id => ({ recruiterId: id }));
      const skills = data?.skills.join(',');
      const finalData = {
        ...data,
        skills: skills, jobLocations: jobLocations, jobOrderDescription: jobOrderDescription, jobOrderRecruiters: jobOrderRecruiters, currency: selectedCurrency.currencyName
      };
      if (showDialogLoading)
        showDialogLoading("Creating the job");
      setDisableSubmitBtn(true);
      await onCreateJob(finalData, close);
      setDisableSubmitBtn(false);
    } catch (e) {
      console.info('...');
    } finally {
      if (hideDialogLoading)
        hideDialogLoading();
    }
  };

  const onError = () => {
    scrollToError('create-job-form');
  };

  return (
    <Paper id='create-job-form'>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(submitHandler, onError)}>
          <div className="flex justify-center items-center w-full h-[60px] py-10 text-[#28364C] font-medium text-base pl-[13px]">
            Create Job
          </div>
          <div className="flex flex-col items-center justify-start gap-10 px-[10%] bg-[#FAFAFA] py-[4%]">
            <div className="w-full flex justify-start text-[#28364C] font-semibold text-base ">
              Job Details:
            </div>
            <div className="flex items-start w-full gap-7">
              <div className="w-[280px] flex-shrink-0">
                <RHFSelect
                  control={methods.control}
                  name="clientId"
                  label="Client"
                  options={clientOptions || []}
                />
              </div>

              <div className="w-[170px] flex-shrink-0">
                <RHFInput name="jobId" control={methods.control} label="Job Id" />
              </div>

            </div>
            <div className="flex items-center w-full gap-7">
              <div className="w-[480px] flex-shrink-0">
                <RHFInput name="jobTitle" control={methods.control} label="Job Title" />
              </div>
            </div>
            <div className="flex items-start w-full gap-7">
              <div className="w-[280px] flex-shrink-0">
                <RHFSelect
                  control={methods.control}
                  name="requirementType"
                  label="Requirement Type"
                  options={[
                    { value: 'directClient', label: 'Client' },
                    { value: 'benchSales', label: 'Bench-Sales' },
                    { value: 'c2c', label: 'C2C' },
                    { value: 'internal', label: 'Internal'},
                  ]}
                />
              </div>

              <div className="w-[170px] flex-shrink-0">
                <RHFInput name="vendor" control={methods.control} label="Vendor" disabled={enableVendor} />
              </div>

            </div>
            <div className="flex items-start w-full gap-7">
              <div className="w-[280px] flex-shrink-0">
                <RHFSelect
                  control={methods.control}
                  name="jobType"
                  label="Job Type"
                  options={[
                    { value: 'contractHire', label: 'Contract-hire' },
                    { value: 'fullTime', label: 'Full Time' },
                    { value: 'Contract', label: 'Contract' },
                  ]}
                />
              </div>

              <div className="w-[170px] flex-shrink-0">
                <RHFInput name="jobDuration" control={methods.control} label="Duration" disabled={enableDuration} />
              </div>
              <div className="w-[200px] flex-shrink-0">
                <RHFInput name="payRate" iconStart="$" control={methods.control} label="Pay Rate" onCurrencyChange={(currency: Option) => setSelectedCurrency(currency)} />
              </div>

            </div>
            <div className="flex items-start w-full gap-7">
              <div className="w-[650px] flex-shrink-0">
                <RHFMultiAutoComplete
                  control={methods.control}
                  name="jobLocations"
                  label="Job Location"
                  options={locationsOptions || []}
                  loading={locationsLoading}
                  placeholder="Search Location"
                  enableVirtualization
                />
              </div>
            </div>
            <div className="flex items-start w-full gap-7">
              <div className="w-[650px] flex-shrink-0">
                <RHFMultiAutoComplete
                  name="skills"
                  label="Skills"
                  control={methods.control}
                  options={skillsOptions || []}
                  loading={skillsLoading}
                  placeholder="Search Skills"
                  enableVirtualization
                />
              </div>
            </div>
            <div className="items-start w-full">
              <div className="items-start w-full text-[#28364C] font-semibold text-base">Job Description</div>
              <RHFEditor
                editorType="classic"
                name="jobOrderDescription"
                control={methods.control}
                disabled={false}
                height={300}
              />
            </div>
            <div className="flex flex-col w-full justify-start">
              <div className="w-[30%]">
                <RHFFile
                  control={methods.control}
                  name="documents"
                  label="Documents"
                  supportTypes={{ [FileFormat.PDF]: [], [FileFormat.JPG]: [], [FileFormat.PNG]: [] }}
                  minSize={1}
                  maxSize={10000000}
                />
              </div>
              <div className="h-4" />
            </div>
            <div className="flex items-center w-full gap-7">
              <div className="w-[650px] flex-shrink-0">
                <RHFMultiAutoComplete
                  control={methods.control}
                  name="jobOrderRecruiters"
                  label="Recruiters"
                  options={recruitersWithEmailOptions || []}
                  loading={recruitersLoading}
                  placeholder="Search Recruiters"
                  enableVirtualization
                />
              </div>
            </div>
            <div className="w-full justify-start">
              <Button variant="outlined" type="submit" className="font-medium rounded-full" size="medium" disabled={disableSubmitBtn}>CREATE JOB</Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </Paper>
  );
};

export default CreateJob;
